import { Copy, CalendarDays, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { COMPANION_API } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";

const CompanionCalendar = () => {
  const { user } = useCompanionAuth();
  const feedUrl = `${COMPANION_API}/calendar/${user?.id}.ics`;
  const webcalUrl = feedUrl.replace(/^https?:\/\//, "webcal://");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.success("Link copied.");
    } catch { toast.error("Couldn't copy — long-press the link to copy."); }
  };

  return (
    <div className="px-4 py-5 space-y-5" data-testid="companion-calendar-page">
      <header>
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Calendar</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">Sync your jobs</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Subscribe to this calendar URL from Apple Calendar, Google Calendar or Outlook and every job you're assigned to will appear automatically.
        </p>
      </header>

      <section className="rounded-lg border border-border bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-crest-green" />
          <h2 className="font-display text-sm font-semibold text-crest-navy">Your subscription URL</h2>
        </div>
        <code className="block break-all text-[11px] bg-crest-surface border border-border rounded p-3 text-crest-navy">{feedUrl}</code>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={copy} className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-crest-green text-white text-sm font-semibold" data-testid="calendar-copy">
            <Copy className="h-4 w-4" /> Copy link
          </button>
          <a href={webcalUrl} className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-crest-navy text-white text-sm font-semibold" data-testid="calendar-subscribe">
            <ExternalLink className="h-4 w-4" /> Subscribe
          </a>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Tip: on iPhone, tap <strong>Subscribe</strong> — Apple Calendar will open and offer to add it. On Android, tap <strong>Copy link</strong>, open Google Calendar → Settings → Add calendar → From URL, and paste.
        </p>
      </section>

      <section className="rounded-lg border border-border bg-white p-4">
        <h2 className="font-display text-sm font-semibold text-crest-navy mb-2">How it works</h2>
        <ul className="text-sm text-crest-navy/80 space-y-1.5 list-disc list-inside">
          <li>Each job assigned to you appears as an event on the date and time it's scheduled.</li>
          <li>Your calendar app refreshes the feed automatically (usually hourly).</li>
          <li>If the office reschedules a job, your calendar updates the next time it refreshes.</li>
        </ul>
      </section>
    </div>
  );
};

export default CompanionCalendar;
